import React from 'react';
import {
  type ColumnDef,
  flexRender,
  type ReactTable,
  type Table,
} from '@tanstack/react-table';
import type { AppTableFeatures } from './tableFeatures';

import { Filter } from './TableFilter';
import { FaSortUp, FaSortDown, FaSort } from 'react-icons/fa';

import { ScrollContainer } from 'react-indiana-drag-scroll';
import NestedTable from './NestedTable';

export default function PaginatedTable<
  T extends { [key: string]: any },
  TChild extends { [key: string]: any } = T,
>({
  table,
  subRowProp,
  // columnResizeMode,
  showFilters = false,
  // alignCells = 'left',
  allowWordWrap = false,
  noEmptyRows = false,
  showPagination = true,
  showFooter = false,
  // striped = true,
  columns2,
}: {
  table: ReactTable<AppTableFeatures, T>;
  subRowProp?: keyof T;
  columnResizeMode: string;
  showFilters?: boolean;
  alignCells?: string;
  allowWordWrap?: boolean;
  noEmptyRows?: boolean;
  showPagination?: boolean;
  showFooter?: boolean;
  striped?: boolean;
  columns2?: ColumnDef<AppTableFeatures, TChild>[];
}) {
  const createEmptyRows = (number: number, table: Table<AppTableFeatures, T>) => {
    const rows = [];
    const tableRow = table.getRowModel().rows[0];
    if (!tableRow) return;
    for (let index = 0; index < number; index++) {
      rows.push(
        <tr
          style={{ height: '33px' }}
          key={`nowRow-${index}`}
          className='border-b odd:bg-white even:bg-gray-50 hover:bg-gray-50 dark:border-gray-700 odd:dark:bg-gray-900 even:dark:bg-gray-800 dark:hover:bg-gray-600'
        >
          {tableRow.getVisibleCells().map((cell) => (
            <td
              key={cell.id}
              {...{
                style: {
                  // maxWidth: cell.column.getSize(),
                },
              }}
            ></td>
          ))}
        </tr>,
      );
    }
    return rows;
  };

  return (
    <div className='w-full overflow-x-hidden'>
      <ScrollContainer
        hideScrollbars={false}
        mouseScroll={{ ignoreElements: 'input, button' }}
        className='indiana-scroll-container pb-4 scrollbar-thin dark:scrollbar-track-gray-700 dark:scrollbar-thumb-zinc-500'
      >
        <table className='relative  w-full  dark:bg-gray-800 table-fixed'>
          <thead className='dark:bg-gray-800'>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    {...{
                      colSpan: header.colSpan,

                      style: {
                        position: 'relative',
                        padding: '5px 7px',
                        width:
                          header.getSize() !== 0 ? header.getSize() : undefined,
                        // minWidth: header.getSize(),
                        // maxWidth: header.getSize(),
                      },
                    }}
                  >
                    {header.isPlaceholder ? null : (
                      <div
                        style={
                          {
                            // maxWidth: header.getSize(),
                            // minWidth: header.getSize(),
                            // width: header.getSize(),
                          }
                        }
                      >
                        <div
                          {...{
                            className: header.column.getCanSort()
                              ? 'cursor-pointer select-none flex items-center w-full dark:text-gray-300'
                              : 'flex items-center',
                            onClick: header.column.getToggleSortingHandler(),
                          }}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                          {(header.column.getCanSort() &&
                            table.getCoreRowModel().rows.length > 1 &&
                            {
                              asc: (
                                <FaSortUp
                                  style={{ marginLeft: '.5rem' }}
                                ></FaSortUp>
                              ),
                              desc: (
                                <FaSortDown
                                  style={{ marginLeft: '.5rem' }}
                                ></FaSortDown>
                              ),
                              false: (
                                <FaSort
                                  style={{ marginLeft: '.5rem' }}
                                ></FaSort>
                              ),
                            }[header.column.getIsSorted() as string]) ??
                            null}
                        </div>
                        {showFilters &&
                        header.column.getCanFilter() &&
                        table.getCoreRowModel().rows.length > 1 ? (
                          <div>
                            <Filter column={header.column} table={table} />
                          </div>
                        ) : showFilters &&
                          table.getCoreRowModel().rows.length > 1 ? (
                          <div
                            style={{
                              height: '31px',
                              // maxWidth: header.getSize(),
                            }}
                          />
                        ) : null}
                      </div>
                    )}
                    {/* <div
                      {...{
                        onMouseDown: header.getResizeHandler(),
                        onTouchStart: header.getResizeHandler(),
                        className: `resizer ${
                          header.column.getIsResizing() ? `isResizing` : ""
                        }`,
                        style: {
                          cursor: "col-resize",
                          transform:
                            columnResizeMode === "onEnd" &&
                            header.column.getIsResizing()
                              ? `translateX(${
                              table.state.columnResizing.deltaOffset
                                }px)`
                              : "",
                        },
                      }}
                    /> */}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row, i) => {
              return (
                <React.Fragment key={i}>
                  <tr
                    key={row.id}
                    className='border-b odd:bg-white even:bg-gray-50 hover:bg-gray-200 dark:border-gray-700 odd:dark:bg-gray-900 even:dark:bg-gray-800 dark:hover:bg-gray-600'
                  >
                    {row.getVisibleCells().map((cell) => {
                      return (
                        <td
                          key={cell.id}
                          {...{
                            style: {
                              overflow: 'hidden',
                              whiteSpace: allowWordWrap ? 'normal' : 'nowrap',
                              textOverflow: allowWordWrap ? '' : 'ellipsis',
                              padding: '5px 7px',
                              width:
                                cell.column.getSize() !== 0
                                  ? cell.column.getSize()
                                  : undefined,
                              // width: cell.column.getSize(),
                              // minWidth: cell.column.getSize(),
                              // maxWidth: cell.column.getSize(),
                            },
                          }}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </td>
                      );
                    })}
                  </tr>
                  {subRowProp && columns2 && row.getIsExpanded() && (
                    <tr>
                      <td colSpan={table.getFlatHeaders().length}>
                        <div className='px-8'>
                          <NestedTable
                            showPagination={
                              row.original[subRowProp!].length > 9
                            }
                            showFilters
                            data={row.original[subRowProp!]}
                            allowWordWrap={true}
                            columns1={columns2}
                          />
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {table.getCoreRowModel().rows.length <
              table.state.pagination.pageSize &&
              !noEmptyRows &&
              createEmptyRows(
                table.state.pagination.pageSize -
                  table.getCoreRowModel().rows.length,
                table,
              )}
          </tbody>
          {showFooter && (
            <tfoot>
              {table.getFooterGroups().map((footerGroup) => (
                <tr key={footerGroup.id}>
                  {footerGroup.headers.map((header) => (
                    <th key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.footer,
                            header.getContext(),
                          )}
                    </th>
                  ))}
                </tr>
              ))}
            </tfoot>
          )}
        </table>
      </ScrollContainer>
      {showPagination && table.getRowModel().rows.length > 0 && (
        <nav
          className='flex-column flex flex-wrap items-center justify-center gap-4 pt-4 sm:flex-row'
          aria-label='Table navigation'
        >
          <ul className=' flex h-8 text-sm '>
            <li>
              <button
                className='ms-0 flex h-8 
                items-center justify-center rounded-s-lg border border-gray-300 bg-white
                px-3 leading-tight  hover:bg-gray-100
                 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-70 dark:border-gray-700 dark:bg-gray-800 
                  dark:hover:bg-gray-700 dark:hover:text-white'
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                Previous
              </button>
            </li>

            <li>
              <div
                aria-current='page'
                className=' flex h-8 
                w-[95px] items-center justify-center
                border border-gray-300 px-3 dark:border-gray-700 dark:bg-gray-800 '
              >
                <span className='mr-2 font-bold'>
                  {table.state.pagination.pageIndex + 1}
                </span>
                of <span className='ml-2'>{table.getPageCount()}</span>
              </div>
            </li>
            <li>
              <select
                className='m-0 block w-full h-8 border border-gray-300 py-1.25 pr-6 text-sm shadow-sm dark:border-gray-700 dark:bg-gray-800 focus:ring-0'
                aria-label='rows per page'
                onChange={(e) => {
                  table.setPageSize(Number(e.target.value));
                }}
                value={table.state.pagination.pageSize}
              >
                <option value='5'>5 rows</option>
                <option value='10'>10 rows</option>
                <option value='20'>20 rows</option>
                <option value='25'>25 rows</option>
                <option value='50'>50 rows</option>
                <option value='100'>100 rows</option>
              </select>
            </li>

            <li>
              <button
                className='flex h-8 w-[79px] items-center justify-center 
                rounded-e-lg border border-gray-300 bg-white px-3 leading-tight
                  hover:bg-gray-100 hover:text-gray-700 
                 disabled:cursor-not-allowed disabled:opacity-70 dark:border-gray-700 dark:bg-gray-800
                   dark:hover:bg-gray-700 dark:hover:text-white'
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              >
                Next
              </button>
            </li>
          </ul>

          <div className='text-sm font-semibold'>
            {table.getFilteredRowModel().flatRows.length ?? 'N/A'} results
          </div>
        </nav>
      )}
    </div>
  );
}
